import React from "react";
import Draw from "ol/interaction/Draw";
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  Divider,
  Paper,
  IconButton,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  Map as MapIcon,
  Add as AddIcon,
  Bookmark as BookmarkIcon,
  LocationOn as LocationOnIcon,
  ExpandLess,
  ExpandMore,
  AdsClick as AdsClickIcon,
} from "@mui/icons-material";
import PinDropIcon from "@mui/icons-material/PinDrop";

/** draw tool drawer panel */
export default function DrawToolPanel(ctx) {
  const {
    drawType,
    setDrawType,
    drawnFeatures,
    setDrawnFeatures,
    mapInstanceRef,
    drawLayerRef,
    drawInteractionRef,
    isDrawingActiveRef,
    isDrawingActive,
    setIsDrawingActive,
    onCloseTool,
  } = ctx;

  return (
                <Box
                  sx={{
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    flexGrow: 1,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, fontWeight: 600, color: "#1976d2" }}
                  >
                    Drawing Tools
                  </Typography>

                  <Box sx={{ mb: 1.5 }}>
                    <Typography
                      variant="body2"
                      sx={{ mb: 0.5, fontWeight: 500 }}
                    >
                      Draw Type
                    </Typography>
                    <Select
                      fullWidth
                      size="small"
                      value={drawType}
                      onChange={(e) => setDrawType(e.target.value)}
                      sx={{ bgcolor: "white" }}
                    >
                      <MenuItem value="Point">Point</MenuItem>
                      <MenuItem value="LineString">Line</MenuItem>
                      <MenuItem value="Polygon">Polygon</MenuItem>
                      <MenuItem value="Circle">Circle</MenuItem>
                    </Select>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* <Box
                    sx={{ mb: 2, p: 1.5, bgcolor: "#e3f2fd", borderRadius: 1 }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "11px", color: "#1976d2" }}
                    >
                      {drawType === "Point" && "📍 Click on map to add a point"}
                      {drawType === "LineString" &&
                        "📏 Click multiple points to draw a line"}
                      {drawType === "Polygon" &&
                        "📐 Click multiple points to draw a polygon"}
                      {drawType === "Circle" &&
                        "⭕ Click and drag to draw a circle"}
                    </Typography>
                  </Box> */}

                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      mb: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ mb: 1, fontWeight: 500, flexShrink: 0 }}
                    >
                      Drawn Features ({drawnFeatures.length})
                    </Typography>
                    <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                      {drawnFeatures.length === 0 ? (
                        <Typography
                          variant="caption"
                          sx={{ color: "#999", fontSize: "11px" }}
                        >
                          No features drawn yet
                        </Typography>
                      ) : (
                        drawnFeatures.map((feature, index) => (
                          <Paper
                            key={index}
                            sx={{
                              p: 1,
                              mb: 1,
                              bgcolor: "#f5f5f5",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, fontSize: "11px" }}
                              >
                                {feature.type}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "12px", color: "#1976d2" }}
                              >
                                Feature #{index + 1}
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (
                                    !feature.olFeature ||
                                    !mapInstanceRef.current
                                  )
                                    return;
                                  const geom = feature.olFeature.getGeometry();
                                  if (!geom) return;
                                  const view = mapInstanceRef.current.getView();
                                  const geomType = geom.getType();
                                  const extent = geom.getExtent();
                                  if (
                                    extent &&
                                    extent.every((v) => isFinite(v))
                                  ) {
                                    const [minX, minY, maxX, maxY] = extent;
                                    if (maxX - minX < 1 && maxY - minY < 1) {
                                      view.animate({
                                        center: [
                                          (minX + maxX) / 2,
                                          (minY + maxY) / 2,
                                        ],
                                        zoom: 15,
                                        duration: 800,
                                      });
                                    } else {
                                      view.fit(extent, {
                                        padding: [100, 100, 100, 100],
                                        duration: 800,
                                      });
                                    }
                                  }
                                }}
                                sx={{
                                  color: "#1976d2",
                                  "&:hover": { bgcolor: "#e3f2fd" },
                                }}
                                title="Zoom to feature"
                              >
                                <SearchIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const feature = drawnFeatures[index];
                                  if (
                                    feature.olFeature &&
                                    drawLayerRef.current
                                  ) {
                                    drawLayerRef.current
                                      .getSource()
                                      .removeFeature(feature.olFeature);
                                  }
                                  setDrawnFeatures(
                                    drawnFeatures.filter((_, i) => i !== index),
                                  );
                                }}
                                sx={{ color: "#f44336" }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Paper>
                        ))
                      )}
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      flexShrink: 0,
                      mb: 2,
                    }}
                  >
                    {!isDrawingActive ? (
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={() => {
                          if (!mapInstanceRef.current || !drawLayerRef.current)
                            return;

                          // Pehle se active draw hatao
                          if (drawInteractionRef.current) {
                            mapInstanceRef.current.removeInteraction(
                              drawInteractionRef.current,
                            );
                          }

                          const draw = new Draw({
                            source: drawLayerRef.current.getSource(),
                            type: drawType,
                          });

                          drawInteractionRef.current = draw;
                          isDrawingActiveRef.current = true; // ← SET
                          setIsDrawingActive(true); // ← UI update
                          mapInstanceRef.current.addInteraction(draw);

                          draw.on("drawend", (evt) => {
                            const geomType = evt.feature
                              .getGeometry()
                              .getType();
                            setDrawnFeatures((prev) => [
                              ...prev,
                              {
                                type: geomType,
                                id: Date.now(),
                                olFeature: evt.feature,
                              },
                            ]);

                            // Point ke baad auto-stop, Line/Polygon continuous mode
                            if (drawType === "Point") {
                              mapInstanceRef.current.removeInteraction(draw);
                              drawInteractionRef.current = null;
                              isDrawingActiveRef.current = false; // ← RESET
                              setIsDrawingActive(false);
                            }
                          });
                        }}
                        sx={{
                          bgcolor: "#1976d2",
                          "&:hover": { bgcolor: "#115293" },
                        }}
                      >
                        Start Drawing
                      </Button>
                    ) : (
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<CloseIcon />}
                        onClick={() => {
                          if (
                            drawInteractionRef.current &&
                            mapInstanceRef.current
                          ) {
                            mapInstanceRef.current.removeInteraction(
                              drawInteractionRef.current,
                            );
                            drawInteractionRef.current = null;
                          }
                          isDrawingActiveRef.current = false; // ← RESET
                          setIsDrawingActive(false);
                        }}
                        sx={{
                          bgcolor: "#ff9800",
                          "&:hover": { bgcolor: "#f57c00" },
                        }}
                      >
                        Stop Drawing
                      </Button>
                    )}

                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => {
                          setDrawnFeatures([]);
                          if (drawLayerRef.current) {
                            drawLayerRef.current.getSource().clear();
                          }
                          if (
                            drawInteractionRef.current &&
                            mapInstanceRef.current
                          ) {
                            mapInstanceRef.current.removeInteraction(
                              drawInteractionRef.current,
                            );
                            drawInteractionRef.current = null;
                          }
                        }}
                        sx={{ borderColor: "#ff9800", color: "#ff9800" }}
                      >
                        Clear All
                      </Button>

                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        onClick={() => {
                          onCloseTool("draw");
                        }}
                        sx={{ borderColor: "#f44336", color: "#f44336" }}
                      >
                        Close
                      </Button>
                    </Box>
                  </Box>
                </Box>
  );
}
